/**
 * ZAdminAiEvaluationBudget — Zod schemas for per-org AI-evaluation budget
 * admin surface.
 *
 * The budget envelope (daily + monthly USD caps + running counters) lives
 * on `Organization.aiEvaluationBudget` as a Json column. Edits flow only
 * through the admin console-ui — the org-side surface displays a
 * read-only summary so operators can self-diagnose "we hit the cap" but
 * cannot lift it without admin intervention.
 *
 * PBAC: `admin:ai-evaluation-budget` (read / write).
 */

import { z } from "zod";

import { AiEvaluationBudgetSchema } from "../../../schemas/workflow-presets/ai-evaluation-budget.schema";
import { ApiSuccessSchema } from "../../common/response.schema";

const AdminAiEvaluationBudgetGetResponseSchema = z.object({
  budget: AiEvaluationBudgetSchema.nullable()
});

/**
 * Caps-only update body. Running counters (`spentToday`, `spentThisMonth`)
 * are NEVER admin-mutable — they're owned by the daily/monthly reset
 * crons + the per-inference increment adapter. Admins set the ceilings;
 * the runtime owns the meters.
 */
const AdminAiEvaluationBudgetSetCapsBodySchema = z.object({
  dailyUsdCap: z.number().min(0),
  monthlyUsdCap: z.number().min(0)
});

const AdminAiEvaluationBudgetSetCapsResponseSchema = z.object({
  budget: AiEvaluationBudgetSchema
});

const AdminAiEvaluationBudgetPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

export const ZAdminAiEvaluationBudget = {
  /**
   * GET /api/admin/organizations/:orgId/ai-evaluation-budget
   *
   * Read the org's AI-evaluation budget envelope. Returns `null` when
   * the org has never had a budget configured.
   */
  GetBudget: {
    method: "GET" as const,
    path: "/api/admin/organizations/:orgId/ai-evaluation-budget",
    PathVars: AdminAiEvaluationBudgetPathVarsSchema,
    Response: ApiSuccessSchema(AdminAiEvaluationBudgetGetResponseSchema)
  },

  /**
   * PUT /api/admin/organizations/:orgId/ai-evaluation-budget/caps
   *
   * Upsert the org's daily + monthly USD caps. Preserves running
   * counters so a cap edit does not silently zero "spent today" mid-day.
   */
  SetCaps: {
    method: "PUT" as const,
    path: "/api/admin/organizations/:orgId/ai-evaluation-budget/caps",
    PathVars: AdminAiEvaluationBudgetPathVarsSchema,
    Body: AdminAiEvaluationBudgetSetCapsBodySchema,
    Response: ApiSuccessSchema(AdminAiEvaluationBudgetSetCapsResponseSchema)
  }
} as const;
